import { useParams, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatCurrency, safeDateFormat } from "@/lib/utils";
import {
  Check, X, ArrowLeft, Wand2, Mail, Search, FileText,
  Eye, ChevronDown, ChevronRight, User, Hash, FileSpreadsheet, Banknote
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { ReceiptPreviewPanel } from "@/components/receipt-preview-panel";
import { useAuth } from "@/hooks/use-auth";
import { hasGroupAdmin } from "@/lib/pagePermissions";

function useStatementData(id: number) {
  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
  return useQuery({
    queryKey: [`/api/statements/${id}`],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/statements/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load statement");
      const json = await res.json();
      return json as { statement: any; entries: any[] };
    },
    enabled: id > 0,
  });
}

export default function StatementDetail() {
  const params = useParams();
  const id = Number(params.id) || 0;
  const { data, isLoading, error, refetch } = useStatementData(id);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  const { user } = useAuth();
  const isAdmin = hasGroupAdmin(user, "expenses");

  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("needs-action");
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const noReceiptMutation = useMutation({
    mutationFn: async ({ entryId, noReceipt }: { entryId: number; noReceipt: boolean }) => {
      const res = await fetch(`${BASE}/api/statements/${id}/entries/${entryId}/no-receipt`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ noReceipt }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || "Failed to update");
      }
      return res.json();
    },
    onSuccess: () => { refetch(); },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const matchMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/statements/${id}/match`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error("Match failed");
      return res.json();
    },
    onSuccess: (result: any) => {
      toast({ title: "Auto-match complete!", description: "Matched " + String(result.matched || 0) + " new entries." });
      refetch();
    },
  });

  const remindMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/statements/${id}/send-reminders`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error("Remind failed");
      return res.json();
    },
    onSuccess: (result: any) => {
      toast({ title: "Reminders sent!", description: "Sent " + String(result.sent || 0) + " emails." });
    },
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="p-8 text-center text-slate-500">{"Loading statement details..."}</div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="p-8 text-center">
          <p className="text-red-500 mb-2">{"Failed to load statement."}</p>
          <Link href="/statements">
            <Button variant="outline" size="sm">{"Back to Statements"}</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const stmt = data.statement || {};
  const allEntries: any[] = Array.isArray(data.entries) ? data.entries : [];

  const stmtFilename = String(stmt.originalFilename || "Statement");
  const stmtDate = stmt.statementDate ? safeDateFormat(String(stmt.statementDate), "MMMM d, yyyy") : "N/A";
  const totalSpending = Number(stmt.totalSpending) || 0;
  const totalEntries = Number(stmt.totalEntries) || 0;
  const matchedCount = Number(stmt.matchedEntries) || 0;
  const matchPct = totalEntries > 0 ? Math.round((matchedCount / totalEntries) * 100) : 0;

  const q = searchTerm.toLowerCase();
  const filtered = q
    ? allEntries.filter((e: any) => {
        const desc = String(e.description || "").toLowerCase();
        const name = String(e.cardholderName || "").toLowerCase();
        return desc.includes(q) || name.includes(q);
      })
    : allEntries;

  const matched = filtered.filter((e: any) => e.matched === true);
  // "Needs Action" = not matched AND not flagged as no-receipt payroll deduction.
  const needsAction = filtered.filter((e: any) => !e.matched && !e.noReceipt);
  // Payroll deductions = not matched but admin-flagged as no receipt required.
  const payrollEntries = filtered.filter((e: any) => !e.matched && e.noReceipt);
  const unmatched = filtered.filter((e: any) => !e.matched);

  let visible: any[];
  if (activeTab === "matched") visible = matched;
  else if (activeTab === "all") visible = filtered;
  else if (activeTab === "payroll") visible = payrollEntries;
  else visible = needsAction;

  const groupMap: Record<string, any[]> = {};
  for (let i = 0; i < visible.length; i++) {
    const name = String(visible[i].cardholderName || "Unknown");
    if (!groupMap[name]) groupMap[name] = [];
    groupMap[name].push(visible[i]);
  }
  const groupNames = Object.keys(groupMap).sort();

  return (
    <Layout>
      <div className="mb-6">
        <Link href="/statements" className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-primary mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-1" />
          <span>{"Back to Statements"}</span>
        </Link>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">{stmtFilename}</h1>
            <p className="text-slate-500 mt-1">{"Statement Date: "}{stmtDate}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => remindMutation.mutate()}
              disabled={remindMutation.isPending || matchPct === 100}
            >
              <Mail className="w-4 h-4 mr-2" />
              <span>{remindMutation.isPending ? "Sending..." : "Send Reminders"}</span>
            </Button>
            <Button
              onClick={() => matchMutation.mutate()}
              disabled={matchMutation.isPending || matchPct === 100}
            >
              <Wand2 className="w-4 h-4 mr-2" />
              <span>{matchMutation.isPending ? "Matching..." : "Run Auto-Match"}</span>
            </Button>
            <Link href={"/statements/" + String(id) + "/nominals"}>
              <Button variant="outline">
                <Hash className="w-4 h-4 mr-2" />
                <span>{"Assign Nominals"}</span>
              </Button>
            </Link>
            <a href={`${BASE}/api/statements/${id}/export-nominals`} download>
              <Button variant="outline">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                <span>{"Export to Excel"}</span>
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">{"Total Spending"}</p>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalSpending)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">{"Match Progress"}</p>
            <div className="flex items-end gap-2">
              <p className="text-2xl font-bold text-slate-900">{String(matchPct) + "%"}</p>
              <p className="text-sm text-slate-500 mb-1">{"(" + String(matchedCount) + "/" + String(totalEntries) + ")"}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">{"Needs Action"}</p>
            <p className="text-2xl font-bold text-amber-600">{String(needsAction.length)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={activeTab === "needs-action" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("needs-action")}
              className="rounded-full"
            >
              {"Needs Action (" + String(needsAction.length) + ")"}
            </Button>
            <Button
              variant={activeTab === "matched" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("matched")}
              className="rounded-full"
            >
              {"Matched (" + String(matched.length) + ")"}
            </Button>
            {payrollEntries.length > 0 && (
              <Button
                variant={activeTab === "payroll" ? "default" : "ghost"}
                size="sm"
                onClick={() => setActiveTab("payroll")}
                className="rounded-full"
              >
                {"Payroll (" + String(payrollEntries.length) + ")"}
              </Button>
            )}
            <Button
              variant={activeTab === "all" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("all")}
              className="rounded-full"
            >
              {"All (" + String(filtered.length) + ")"}
            </Button>
          </div>
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search entries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
        </div>

        {groupNames.length === 0 && (
          <div className="px-6 py-16 text-center text-slate-500">
            {activeTab === "needs-action"
              ? "All entries are matched or marked for payroll deduction!"
              : activeTab === "payroll"
                ? "No entries marked as payroll deduction."
                : "No transactions found."}
          </div>
        )}

        {groupNames.map((groupName) => {
          const groupEntries = groupMap[groupName];
          const isCollapsed = collapsedGroups[groupName] !== false;
          const groupTotal = groupEntries.reduce((s: number, e: any) => s + (Number(e.amount) || 0), 0);

          return (
            <div key={groupName}>
              <div
                className="px-6 py-3 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors flex items-center justify-between"
                onClick={() => {
                  setCollapsedGroups((prev) => ({
                    ...prev,
                    [groupName]: prev[groupName] === false ? true : false,
                  }));
                }}
              >
                <div className="flex items-center gap-3">
                  {isCollapsed
                    ? <ChevronRight className="w-4 h-4 text-slate-400" />
                    : <ChevronDown className="w-4 h-4 text-slate-400" />
                  }
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="font-semibold text-slate-800">{groupName}</span>
                  <span className="text-xs text-slate-500">
                    {String(groupEntries.length) + (groupEntries.length === 1 ? " entry" : " entries")}
                  </span>
                </div>
                <span className="text-sm text-slate-500">{formatCurrency(groupTotal)}</span>
              </div>

              {!isCollapsed && groupEntries.map((entry: any) => {
                const entryId = Number(entry.id) || 0;
                const entryDesc = String(entry.description || "");
                const entryDate = safeDateFormat(String(entry.transactionDate || ""), "MMM d, yyyy");
                const entryAmount = Number(entry.amount) || 0;
                const isMatched = entry.matched === true;
                const isPayroll = !isMatched && entry.noReceipt === true;
                const receiptLabel = entry.matchedReceipt
                  ? String(entry.matchedReceipt.vendor || entry.matchedReceipt.originalFilename || "Receipt")
                  : "";
                const isPending = noReceiptMutation.isPending && (noReceiptMutation.variables as any)?.entryId === entryId;

                return (
                  <div
                    key={entryId}
                    className={`px-6 py-4 border-b border-slate-100 transition-colors ${isPayroll ? "bg-purple-50/40" : "cursor-pointer hover:bg-slate-50/80"}`}
                    onClick={isPayroll ? undefined : () => { setSelectedEntry(entry); setPanelOpen(true); }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="shrink-0">
                        {isMatched ? (
                          <div className="w-9 h-9 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                            <Check className="w-5 h-5" />
                          </div>
                        ) : isPayroll ? (
                          <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                            <Banknote className="w-5 h-5" />
                          </div>
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
                            <X className="w-5 h-5" />
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{entryDesc}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span>{entryDate}</span>
                          {isMatched && receiptLabel !== "" && (
                            <span className="inline-flex items-center gap-1 text-green-600">
                              <FileText className="w-3 h-3" />
                              <span>{receiptLabel}</span>
                            </span>
                          )}
                          {isPayroll && (
                            <span className="inline-flex items-center gap-1 text-purple-600 font-medium">
                              <Banknote className="w-3 h-3" />
                              Payroll deduction
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {/* Admin no-receipt toggle */}
                        {isAdmin && !isMatched && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              noReceiptMutation.mutate({ entryId, noReceipt: !isPayroll });
                            }}
                            disabled={isPending}
                            title={isPayroll ? "Remove payroll deduction flag" : "Mark as payroll deduction (no receipt required)"}
                            className={`text-xs px-2 py-1 rounded-lg border transition-colors disabled:opacity-50 ${
                              isPayroll
                                ? "bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200"
                                : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200"
                            }`}
                          >
                            {isPending ? "…" : isPayroll ? "Undo payroll" : "No receipt"}
                          </button>
                        )}
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">{formatCurrency(entryAmount)}</p>
                          {isMatched
                            ? <span className="text-xs text-green-600">Matched</span>
                            : isPayroll
                              ? <span className="text-xs text-purple-600">Payroll deduction</span>
                              : <span className="text-xs text-amber-600">No receipt</span>
                          }
                        </div>
                        {!isPayroll && <Eye className="w-4 h-4 text-slate-300" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <ReceiptPreviewPanel
        open={panelOpen}
        onOpenChange={(v) => { setPanelOpen(v); if (!v) setSelectedEntry(null); }}
        entry={selectedEntry}
        statementId={id}
        onRefetch={refetch}
      />
    </Layout>
  );
}
